import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic, Plus } from "lucide-react";
import { api } from "../../api";
import { useProfile } from "../../layout/AppShell";
import { fadeUp, stagger } from "../../lib/motion";
import { cn } from "../../lib/format";

// Each visible `quote` here MUST match the line that was synthesized
// into /public/personas/<id>.mp3 by scripts/generate_persona_samples.py.
// Keep these two files in lock-step — what the user reads on a card has
// to match what the "hear voice" button plays.
const PERSONAS = [
  {
    id: "casual",
    name: "Casual",
    tagline: "a friend who happens to know your language",
    quote: "Morgen. Kaffee, oder schon zur Arbeit? I'd pick Kaffee.",
    en: "Morning. Coffee, or already off to work? I'd pick coffee.",
    traits: ["warm", "forgiving", "lots of filler"],
  },
  {
    id: "grumpy",
    name: "Grumpy Old Timer",
    tagline: "corrects you. plainly. doesn't coddle.",
    quote: "Hallo. Three mistakes already. Try again — slower this time.",
    en: "Hello. Three mistakes already. Try again — slower this time.",
    traits: ["terse", "no smalltalk", "fixes your articles"],
  },
  {
    id: "optimist",
    name: "Cheerful Optimist",
    tagline: "annoyingly upbeat, in the best way",
    quote: "Hallo! You're doing amazing — three new words today, and it's only Tuesday!",
    en: "Hello! You're doing amazing — three new words today, and it's only Tuesday!",
    traits: ["loud", "celebratory", "exclamation-prone"],
  },
  {
    id: "philosopher",
    name: "Deep Thinker",
    tagline: "questions everything, even coffee",
    quote: "But why do we say guten Morgen and not schöner Morgen? What is a good morning, really?",
    en: "But why do we say a “good” morning and not a “lovely” one? What is a good morning, really?",
    traits: ["probing", "tangents", "never lectures"],
  },
  {
    id: "gossip",
    name: "Nosy Friend",
    tagline: "asks more than answers",
    quote: "So — what happened with that Feierabend last night? Spill.",
    en: "So — what happened with that end-of-workday last night? Spill.",
    traits: ["follow-ups", "remembers names", "weirdly personal"],
  },
  {
    id: "minimalist",
    name: "Dry Minimalist",
    tagline: "says little, means a lot",
    quote: "Hallo. Ready?",
    en: "Hello. Ready?",
    traits: ["clipped", "pregnant pauses", "high signal"],
  },
];

export default function Personas() {
  const { profile, setProfile } = useProfile();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [noAudio, setNoAudio] = useState<Set<string>>(new Set());

  async function choose(id: string) {
    if (busy) return;
    setBusy(id);
    setErr(null);
    try {
      const updated = await api.updateMe({ persona: id });
      setProfile(updated);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "update failed");
    } finally {
      setBusy(null);
    }
  }

  function playSample(id: string) {
    if (noAudio.has(id) || playing === id) return;
    setPlaying(id);
    const audio = new Audio(`/personas/${id}.mp3`);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => {
      setPlaying(null);
      setNoAudio((s) => new Set(s).add(id));
    };
    audio.play().catch(() => {
      setPlaying(null);
      setNoAudio((s) => new Set(s).add(id));
    });
  }

  const current = profile.persona || "casual";

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.06)}>
      <motion.div variants={fadeUp}>
        <h1 className="font-serif text-3xl md:text-4xl leading-tight">Personas</h1>
        <p className="mt-2 font-serif italic text-ink-soft text-base md:text-lg">
          Switch any time. Your vocabulary follows.
        </p>
      </motion.div>

      {err && <div className="mt-4 text-terracotta text-sm">{err}</div>}

      <motion.div
        variants={stagger(0.05)}
        className="mt-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {PERSONAS.map((p) => {
          const active = p.id === current;
          const isPlaying = playing === p.id;
          return (
            <motion.button
              key={p.id}
              variants={fadeUp}
              type="button"
              onClick={() => !active && choose(p.id)}
              className={cn(
                "relative rounded-md p-6 text-left transition-all duration-200 flex flex-col",
                "border bg-paper",
                active
                  ? "border-terracotta border-2 bg-terracotta-tint"
                  : "border-[rgba(42,36,26,0.15)] hover:border-terracotta/50 hover:-translate-y-0.5",
              )}
            >
              {active && (
                <div className="absolute -top-2.5 left-5 px-2 py-0.5 bg-terracotta text-paper font-mono text-[9px] tracking-[0.16em] uppercase rounded">
                  active
                </div>
              )}

              <div className="font-serif text-2xl leading-tight">{p.name}</div>
              <div className="font-serif italic text-ink-soft text-[13px] mt-1">
                {p.tagline}
              </div>

              {/* Dark inset speech card — quote must match /personas/{id}.mp3 verbatim */}
              <div className="mt-4 rounded-md bg-ink-card text-paper p-4">
                <p className="font-serif italic text-[15px] leading-snug text-paper">
                  &ldquo;{p.quote}&rdquo;
                </p>
                <p className="font-mono text-[11px] text-paper/60 mt-2 leading-snug">
                  {p.en}
                </p>
              </div>

              {/* Traits chips */}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.traits.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 rounded-full bg-paper-soft border border-[rgba(42,36,26,0.12)] font-mono text-[10px] tracking-wide text-ink-soft"
                  >
                    {t}
                  </span>
                ))}
              </div>

              {/* Footer: hear voice + tap-card hint */}
              <div className="mt-auto pt-5 flex items-center gap-3">
                {!noAudio.has(p.id) ? (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Hear voice sample"
                    onClick={(e) => { e.stopPropagation(); playSample(p.id); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        playSample(p.id);
                      }
                    }}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-paper text-ink-soft text-[11px] cursor-pointer",
                      "border-[rgba(42,36,26,0.18)] hover:border-terracotta hover:text-terracotta transition-colors",
                      isPlaying && "border-terracotta text-terracotta",
                    )}
                  >
                    <Mic size={11} className={isPlaying ? "animate-pulse" : ""} />
                    {isPlaying ? "playing…" : "hear voice"}
                  </span>
                ) : (
                  <span className="font-mono text-[10px] text-ink-faint">sample coming soon</span>
                )}
                {!active && (
                  <span className="ml-auto font-mono text-[10px] text-ink-faint">
                    {busy === p.id ? "switching…" : "tap card to switch"}
                  </span>
                )}
              </div>
            </motion.button>
          );
        })}

        {/* Design your own — opens the slider builder */}
        <motion.div variants={fadeUp}>
          <Link
            to="/app/personas/new"
            className="rounded-md border-2 border-dashed border-[rgba(42,36,26,0.20)] p-6 flex flex-col items-center justify-center text-center min-h-[260px] text-ink-soft hover:border-terracotta hover:text-terracotta transition-colors bg-paper/40"
          >
            <div className="w-9 h-9 rounded-full border border-terracotta/40 text-terracotta flex items-center justify-center">
              <Plus size={16} />
            </div>
            <div className="mt-3 font-serif text-xl">Design your own</div>
            <div className="mt-1 font-mono text-[10px] tracking-wide text-ink-faint uppercase">
              five sliders, no name
            </div>
          </Link>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
